import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { MetricCard } from "@/components/ui/MetricCard";
import { Toolbar } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { request } from "@/lib/api/client";
import { useAction, useItem, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, qty, relative, text } from "@/lib/format";

/**
 * Menejer qarorlari.
 *
 * Xodim farqni qayd etgach bo'shaydi va keyingi ishni oladi —
 * hujjat esa menejerni kutadi. Bu ekran o'sha navbat:
 *
 *  * Nazorat (P07-004): manbalar va muqobillar tugagan kamomad.
 *    Qaror buyurtmani o'zi jo'natishga chiqaradi — boshqa ochiq
 *    farq qolmagan bo'lsa.
 *
 * Eski «qabul farqini kelishish» (1C xarid buyurtmasidan qabul)
 * olib tashlandi (2026-11-01): prixod endi erkin sessiya, nakladnoy
 * bilan farq «Qabul sessiyalari» sahifasida ko'rinadi. Bu yerda
 * faqat nakladnoy kutayotgan sessiyalar soni.
 *
 * Sabab MAJBURIY va auditda qoladi. Vakolatni server tekshiradi.
 */

interface ControlSession {
  id: string;
  order_number?: string;
  status: string;
  warehouse_code?: string;
  station_code?: string;
  updated_at: string;
}

interface Discrepancy {
  id: string;
  session_id: string;
  discrepancy_type: string;
  sku_id: string;
  planned_qty?: string;
  counted_qty?: string;
  remaining_qty?: string;
  status: string;
  retry_result?: string;
  updated_at: string;
}

type DecisionRow = Discrepancy & { session: ControlSession };

interface Sku {
  article?: string;
  name?: string;
}

type Decision = "SHIP_PARTIAL" | "CANCEL_LINE" | "WAIT_SUPPLY" | "WRITE_OFF";

/** Qaror shu holatlarda so'raladi (control-service, P07-004). */
const WAITING = ["WAITING_MANAGER", "WAITING_INVENTORY"];

const DECISIONS: Array<{ value: Decision; label: string; hint: string }> = [
  {
    value: "SHIP_PARTIAL",
    label: "Qisman jo'natish",
    hint: "Faqat 1C da tasdiqlangan buyurtma o'zgarishi bilan (P07-17).",
  },
  { value: "CANCEL_LINE", label: "SKU ni bekor qilish", hint: "Yetmagan qism buyurtmadan olinadi." },
  { value: "WRITE_OFF", label: "Hisobdan chiqarish", hint: "Kamomad yo'qotish sifatida yoziladi." },
  {
    value: "WAIT_SUPPLY",
    label: "Ta'minotni kutish",
    hint: "Buyurtma jo'natilmaydi — tovar kelguncha kutadi.",
  },
];

export function Decisions() {
  const session = useSession();
  const canControl = session.can(PERM.controlRead);
  const canInbound = session.can(PERM.inboundRead);
  const [search, setSearch] = useState("");
  const [deciding, setDeciding] = useState<DecisionRow | null>(null);
  const [decision, setDecision] = useState<Decision>("CANCEL_LINE");
  const [changeRef, setChangeRef] = useState("");

  // --- Nazorat ---
  const sessionPath = "/api/v1/control/sessions";
  const sessionLists = [
    useList<ControlSession>("control", sessionPath, { status: WAITING[0], limit: 100 }, { enabled: canControl }),
    useList<ControlSession>("control", sessionPath, { status: WAITING[1], limit: 100 }, { enabled: canControl }),
  ];
  const sessions = sessionLists.flatMap((query) => query.data?.items ?? []);
  const discrepancyQueries = useQueries({
    queries: sessions.map((item) => {
      const path = `/api/v1/control/sessions/${item.id}/discrepancies`;
      return {
        queryKey: ["control", path],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          request<{ items: Discrepancy[] }>("control", path, { signal }),
      };
    }),
  });
  const decisionRows: DecisionRow[] = sessions.flatMap((item, index) =>
    (discrepancyQueries[index]?.data?.items ?? [])
      .filter((row) => WAITING.includes(row.status))
      .map((row) => ({ ...row, session: item })),
  );
  const controlLoading =
    sessionLists.some((query) => query.isLoading) || discrepancyQueries.some((query) => query.isLoading);

  const decide = useAction<Discrepancy, { row: DecisionRow; note: string }>("control", ({ row, note }) => ({
    path: `/api/v1/control/${row.session_id}/manager-decision`,
    method: "POST",
    body: {
      operation_id: crypto.randomUUID(),
      discrepancy_id: row.id,
      decision,
      note,
      order_change_ref: decision === "SHIP_PARTIAL" ? changeRef.trim() : undefined,
    },
  }));

  // --- Qabul: 1C nakladnoyini kutayotgan erkin sessiyalar ---
  const awaitingInvoice = useList<{ id: string }>(
    "inbound",
    "/api/v1/receiving-sessions",
    { status: "CLOSED", limit: 200 },
    { enabled: canInbound },
  );

  const needle = search.trim().toLowerCase();
  const matches = (...values: Array<string | undefined>) =>
    !needle || values.some((value) => value?.toLowerCase().includes(needle));

  const controlColumns: Array<Column<DecisionRow>> = [
    {
      key: "order",
      header: "Buyurtma",
      render: (row) => <span className="mono">{text(row.session.order_number)}</span>,
    },
    { key: "sku", header: "Tovar", render: (row) => <SkuName id={row.sku_id} /> },
    { key: "type", header: "Farq", render: (row) => <StatusBadge value={row.discrepancy_type} /> },
    { key: "planned", header: "Terilgan", align: "right", render: (row) => qty(row.planned_qty) },
    { key: "counted", header: "Sanaldi", align: "right", render: (row) => qty(row.counted_qty) },
    {
      key: "remaining",
      header: "Yetmaydi",
      align: "right",
      render: (row) => <b>{qty(row.remaining_qty)}</b>,
    },
    { key: "status", header: "Holat", render: (row) => <StatusBadge value={row.status} /> },
    {
      key: "waiting",
      header: "Kutmoqda",
      render: (row) => <span title={dateTime(row.updated_at)}>{relative(row.updated_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) =>
        session.can(PERM.managerDecision) ? (
          <button
            type="button"
            className="btn btn-tonal btn-sm"
            onClick={() => {
              setDecision("CANCEL_LINE");
              setChangeRef("");
              setDeciding(row);
            }}
          >
            <Icon name="check" size={14} />
            Qaror
          </button>
        ) : null,
    },
  ];

  const controlRows = decisionRows.filter((row) =>
    matches(row.session.order_number, row.session.warehouse_code),
  );

  return (
    <>
      <PageHeader title="Menejer qarorlari" hint={HINT} />

      <section className="metrics">
        <MetricCard
          label="Nazorat farqlari"
          value={decisionRows.length}
          hint="Jo'natish qaroringizni kutmoqda"
          accent={decisionRows.length ? "warning" : "success"}
          loading={canControl && controlLoading}
          icon="discrepancy"
        />
        <MetricCard
          label="Nakladnoy kutayotgan qabul"
          value={awaitingInvoice.data?.items.length ?? 0}
          hint="«Qabul sessiyalari» da 1C nakladnoyini biriktiring"
          accent={awaitingInvoice.data?.items.length ? "warning" : "success"}
          loading={canInbound && awaitingInvoice.isLoading}
          icon="warehouse"
        />
      </section>

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Buyurtma raqami…"
        onRefresh={() => {
          sessionLists.forEach((query) => void query.refetch());
          void awaitingInvoice.refetch();
        }}
        refreshing={sessionLists.some((query) => query.isFetching) || awaitingInvoice.isFetching}
      />

      <DataTable
        columns={controlColumns}
        rows={controlRows}
        rowKey={(row) => row.id}
        isLoading={controlLoading}
        error={sessionLists.find((query) => query.error)?.error}
        onRetry={() => sessionLists.forEach((query) => void query.refetch())}
        emptyTitle="Qaror kutayotgan farq yo'q"
        emptyHint="Nazoratchi manba va muqobillarni sinab bo'lgach, yechilmagan kamomad shu yerga tushadi."
        footer={<span>{controlRows.length} ta farq</span>}
      />

      <ConfirmDialog
        open={deciding !== null}
        title="Kamomad bo'yicha qaror"
        description={
          deciding ? (
            <>
              <b>{deciding.session.order_number}</b>: {qty(deciding.remaining_qty)} yetmaydi. Qarordan keyin
              boshqa ochiq farq qolmagan bo'lsa buyurtma jo'natishga o'zi chiqadi.
            </>
          ) : null
        }
        confirmLabel="Qaror berish"
        requireReason
        reasonLabel="Izoh"
        reasonPlaceholder="Masalan: mijoz qisman jo'natishga rozi"
        busy={decide.isPending}
        error={decide.error}
        confirmDisabled={decision === "SHIP_PARTIAL" && changeRef.trim() === ""}
        extra={
          <>
            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <span className="field-label">Qaror</span>
              {DECISIONS.map((option) => (
                <label key={option.value} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "4px 0" }}>
                  <input
                    type="radio"
                    name="decision"
                    value={option.value}
                    checked={decision === option.value}
                    onChange={() => setDecision(option.value)}
                  />
                  <span>
                    {option.label}
                    <span className="field-hint" style={{ display: "block" }}>
                      {option.hint}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
            {decision === "SHIP_PARTIAL" && (
              <label className="field">
                <span className="field-label">1C buyurtma o'zgarishi</span>
                <input
                  className="input"
                  value={changeRef}
                  onChange={(event) => setChangeRef(event.target.value)}
                  placeholder="Hujjat raqami yoki GUID"
                />
              </label>
            )}
          </>
        }
        onClose={() => {
          setDeciding(null);
          decide.reset();
        }}
        onConfirm={(note) => {
          if (!deciding) return;
          decide.mutate(
            { row: deciding, note },
            {
              onSuccess: () => {
                setDeciding(null);
                decide.reset();
              },
            },
          );
        }}
      />

    </>
  );
}

const HINT =
  "Xodim farqni qayd etib keyingi ishga o'tgan; hujjat sizning qaroringizni kutadi. Qaror hujjatni o'zi yakunlaydi.";

/** SKU artikuli va nomi — master-data dan, keshlanadi. */
function SkuName({ id }: { id: string }) {
  const sku = useItem<Sku>("masterData", `/api/v1/skus/${id}`);
  if (!sku.data) return <span className="mono">{id.slice(0, 8)}</span>;
  return (
    <span>
      <span className="mono">{text(sku.data.article)}</span> {text(sku.data.name)}
    </span>
  );
}
